import { Fragment, MouseEventHandler, ReactNode, useEffect, useRef } from 'react';
import { Text } from '@visx/text';
import { GridLines } from '@visx/grid/lib/types';

import { common, model } from '@gosupersimple/types';

import { FloatingPortal, useFloating, offset, shift, flip } from '@floating-ui/react';

import { getFormattedDateForPeriod } from '@/lib/date';
import { isNil } from '@/lib/utils';
import { TooltipBase } from '@/components/tooltip';
import { formatPropertyValue } from '@/explore/utils/format';

import { TimeAggregationPeriod } from '../../types';

import styles, { lineColor } from './charts.module.scss';

const TooltipMouseOffset = 15;

export type BarTooltipData = {
  label: string | null;
  value: number | null;
  color?: string;
  type: model.PropertyType | null;
  format?: common.PropertyValueFormat;
  isHovered?: boolean;
};

type TimeTooltipData = {
  label: Date;
  timezone: string;
  value: number | null;
  aggPeriod: TimeAggregationPeriod;
  type: model.PropertyType | null;
  format?: common.PropertyValueFormat;
  isHovered?: boolean;
};
export type TooltipData = BarTooltipData | TimeTooltipData;

const isTimeTooltip = (tooltipData: TooltipData): tooltipData is TimeTooltipData =>
  tooltipData.label instanceof Date;

interface LabelProps {
  x: number;
  textAnchor?: 'middle';
  verticalAnchor?: 'middle';
  y: number;
  hideTooltip: () => void;
  label: string;
  value: number | null;
  width?: number;
  onMouseOver?: MouseEventHandler;
  onClick?: MouseEventHandler;
}

export const Label = ({
  x,
  y,
  textAnchor,
  verticalAnchor,
  hideTooltip,
  label,
  width = 100,
  onMouseOver,
  onClick,
}: LabelProps) => (
  <Text
    verticalAnchor={verticalAnchor}
    x={x}
    textAnchor={textAnchor}
    y={y}
    className={styles.label}
    width={width}
    onMouseMove={onMouseOver}
    onMouseOut={hideTooltip}
    onClick={onClick}
    scaleToFit="shrink-only"
    cursor={onClick ? 'pointer' : 'default'}>
    {label ?? '-'}
  </Text>
);

interface ChartTooltipProps {
  title?: React.ReactNode;
  position: { x: number; y: number };
  children?: ReactNode;
  onDismiss?: () => void;
}

export const ChartTooltip = ({ title, position, children, onDismiss }: ChartTooltipProps) => {
  const positionRef = useRef(position);

  useEffect(() => {
    positionRef.current = position;
  }, [position]);

  const { refs, floatingStyles, update } = useFloating({
    placement: 'right-start',
    strategy: 'fixed',
    middleware: [
      offset({ mainAxis: TooltipMouseOffset, crossAxis: 0 }),
      shift({ padding: 8 }),
      flip({ fallbackPlacements: ['left-start', 'top-start', 'bottom-start'] }),
    ],
  });

  // Add scroll listener to dismiss tooltip on scroll
  useEffect(() => {
    if (!onDismiss) {
      return;
    }

    window.addEventListener('scroll', onDismiss, { passive: true, capture: true });
    return () => {
      window.removeEventListener('scroll', onDismiss, { capture: true });
    };
  }, [onDismiss]);

  useEffect(() => {
    const virtualElement = {
      getBoundingClientRect: () => ({
        width: 0,
        height: 0,
        x: positionRef.current.x,
        y: positionRef.current.y,
        top: positionRef.current.y,
        left: positionRef.current.x,
        right: positionRef.current.x,
        bottom: positionRef.current.y,
      }),
    };

    refs.setReference(virtualElement);
  }, [refs]);

  useEffect(() => {
    update();
  }, [position.x, position.y, update]);

  return (
    <FloatingPortal>
      <div ref={refs.setFloating} className={styles.tooltip} id="tooltip" style={floatingStyles}>
        <TooltipBase title={title}>{children}</TooltipBase>
      </div>
    </FloatingPortal>
  );
};

interface ChartTooltipItemProps {
  label: string;
  value: string | ReactNode;
  color?: string;
  isHovered?: boolean;
}

export const ChartTooltipItem = ({
  label,
  value,
  color,
  isHovered = false,
}: ChartTooltipItemProps) => (
  <li className={isHovered ? styles.hoveredTooltipItem : undefined}>
    {color !== null && color !== undefined && (
      <span className={styles.seriesMarker} style={{ background: color }} />
    )}
    {label}: <strong>{value}</strong>
  </li>
);

interface ChartTooltipSingleValueProps {
  tooltipOpen: boolean;
  position: { x: number; y: number };
  tooltipData: TooltipData | undefined;
  categoryLabel: string;
  valueLabel: string;
  seriesColor?: string;
}

export const ChartTooltipSingleValue = ({
  tooltipOpen,
  position,
  tooltipData,
  valueLabel,
  categoryLabel,
  seriesColor,
}: ChartTooltipSingleValueProps) =>
  tooltipOpen && tooltipData ? (
    <FloatingPortal>
      <ChartTooltip
        position={position}
        title={
          isTimeTooltip(tooltipData)
            ? getFormattedDateForPeriod(
                tooltipData.label,
                tooltipData.aggPeriod,
                tooltipData.timezone,
              )
            : categoryLabel +
              ': ' +
              (isNil(tooltipData.label) || tooltipData.label === '' ? '-' : tooltipData.label)
        }>
        <ul>
          <ChartTooltipItem
            label={valueLabel}
            value={formatPropertyValue(tooltipData.value, {
              type: tooltipData.type,
              format: tooltipData.format,
            })}
            color={seriesColor ?? lineColor}
            isHovered={tooltipData.isHovered}
          />
        </ul>
      </ChartTooltip>
    </FloatingPortal>
  ) : null;

export const renderGridColumns = (
  { lines }: { lines: GridLines },
  options: { width: number; strokeColor: string },
) =>
  lines.map((line, i) =>
    line.from.x === 0 || line.from.x === options.width ? (
      <Fragment key={i} />
    ) : (
      <line
        key={i}
        x1={line.from.x}
        y1={line.from.y}
        x2={line.to.x}
        y2={line.to.y}
        fill="transparent"
        stroke={options.strokeColor}
        strokeWidth="1"
      />
    ),
  );

export type HighlightedData = {
  date: Date;
  group: string | undefined;
  axis: 'left' | 'right' | undefined;
  seriesKey?: string;
  chartType?: 'stacked' | 'grouped';
  point: {
    x: number;
    y: number;
  };
};

interface SeriesDetectionData {
  key: string;
  value: number | null;
  chartType: 'line' | 'area' | 'bar';
  axis: 'left' | 'right';
}

interface SeriesDetectionConfig {
  mouseX?: number;
  mouseY: number;
  barPositions?: Array<{ key: string; x: number; width: number }>;
  isStacked?: boolean;
}

const isWithinSeriesBounds = (mouseY: number, seriesY: number, zeroLineY: number): boolean => {
  const minY = Math.min(seriesY, zeroLineY);
  const maxY = Math.max(seriesY, zeroLineY);
  return mouseY >= minY && mouseY <= maxY;
};

const detectGroupedBarSeries = (
  config: SeriesDetectionConfig,
  validSeries: SeriesDetectionData[],
  valueScales: { left: any; right: any },
): string | undefined | null => {
  const { mouseX, mouseY, barPositions } = config;

  if (mouseX === undefined || !barPositions || barPositions.length === 0) {
    return undefined;
  }

  const barSeries = validSeries.filter((s) => s.chartType === 'bar');
  if (barSeries.length <= 1) {
    return undefined;
  }

  const zeroY = {
    left: valueScales.left(0),
    right: valueScales.right(0),
  };

  const hoveredBarPos = barPositions.find(
    (barPos) => mouseX >= barPos.x && mouseX <= barPos.x + barPos.width,
  );

  if (!hoveredBarPos) {
    return null;
  }

  const series = barSeries.find((s) => s.key === hoveredBarPos.key);
  if (!series) {
    return null;
  }

  const valueScale = valueScales[series.axis];
  const seriesY = valueScale(series.value ?? 0);
  const zeroLineY = zeroY[series.axis];

  if (isWithinSeriesBounds(mouseY, seriesY, zeroLineY)) {
    return series.key;
  }

  // Mouse is over a bar's X position but not within its Y bounds (e.g., above/below the bar)
  // Return null to prevent fallback detection from incorrectly selecting this bar
  return null;
};

const detectStackedSeries = (
  config: SeriesDetectionConfig,
  validSeries: SeriesDetectionData[],
  valueScales: { left: any; right: any },
): string | undefined => {
  const { mouseY, mouseX, barPositions } = config;

  // Group by axis and calculate cumulative values for stacking
  const axesData: { left: SeriesDetectionData[]; right: SeriesDetectionData[] } = {
    left: [],
    right: [],
  };

  validSeries.forEach((series) => {
    axesData[series.axis].push(series);
  });

  // Check if we're hovering over bars - if so, check X position first
  const barSeries = validSeries.filter((s) => s.chartType === 'bar');
  const hasBarPositions = barPositions !== undefined && barPositions.length > 0;

  // For bar stacks, check X position first and return early if not over a bar
  if (barSeries.length > 0 && hasBarPositions && mouseX !== undefined) {
    // Find which bar we're hovering over (if any) based on X position
    // Note: All segments in a stack share the same X position
    const hoveredBar = barPositions.find(
      (barPos) => mouseX >= barPos.x && mouseX <= barPos.x + barPos.width,
    );

    // If not hovering over any bar X position, return undefined
    if (!hoveredBar) {
      return undefined;
    }
  }

  // Now check Y position to find which segment in the stack
  for (const axis of ['left', 'right'] as const) {
    const axisData = axesData[axis];
    if (axisData.length === 0) {
      continue;
    }

    const valueScale = valueScales[axis];
    let cumulativeValue = 0;

    for (let i = axisData.length - 1; i >= 0; i--) {
      const series = axisData[i];
      const value = series.value ?? 0;

      const stackBottom = cumulativeValue;
      const stackTop = cumulativeValue + value;
      const isWithinSegment = mouseY >= valueScale(stackTop) && mouseY <= valueScale(stackBottom);

      if (isWithinSegment) {
        return series.key;
      }

      cumulativeValue += value;
    }
  }

  return undefined;
};

const detectStandardSeries = (
  config: SeriesDetectionConfig,
  validSeries: SeriesDetectionData[],
  valueScales: { left: any; right: any },
): string | undefined => {
  const { mouseY, mouseX, barPositions } = config;

  const zeroY = {
    left: valueScales.left(0),
    right: valueScales.right(0),
  };

  // Find the series whose boundary the mouse is closest to
  let bestSeries = null;
  let bestDistance = Infinity;

  // Process series in reverse order so that when multiple series have identical values,
  // we pick the one rendered last (on top in SVG)
  for (let i = validSeries.length - 1; i >= 0; i--) {
    const series = validSeries[i];
    const valueScale = valueScales[series.axis];
    const seriesY = valueScale(series.value ?? 0);
    const zeroLineY = zeroY[series.axis];

    if (series.chartType === 'bar') {
      if (mouseX !== undefined && barPositions && barPositions.length > 0) {
        const barPos = barPositions.find((bp) => bp.key === series.key);

        // Skip this bar if not within X bounds
        if (barPos && !(mouseX >= barPos.x && mouseX <= barPos.x + barPos.width)) {
          continue;
        }
      }

      if (isWithinSeriesBounds(mouseY, seriesY, zeroLineY)) {
        const distanceToBoundary = Math.abs(mouseY - seriesY);

        if (distanceToBoundary <= bestDistance) {
          bestDistance = distanceToBoundary;
          bestSeries = series;
        }
      }
    } else if (series.chartType === 'area') {
      if (isWithinSeriesBounds(mouseY, seriesY, zeroLineY)) {
        const distanceToBoundary = Math.abs(mouseY - seriesY);

        if (distanceToBoundary <= bestDistance) {
          bestDistance = distanceToBoundary;
          bestSeries = series;
        }
      }
    } else if (series.chartType === 'line') {
      // For lines, use a tolerance and distance to the line
      const LINE_TOLERANCE = 3;
      const distanceToLine = Math.abs(mouseY - seriesY);

      if (distanceToLine <= LINE_TOLERANCE && distanceToLine < bestDistance) {
        bestDistance = distanceToLine;
        bestSeries = series;
      }
    }
  }

  return bestSeries?.key;
};

// Fallback detection for when no series boundaries contain the mouse
const detectFallbackSeries = (
  validSeries: SeriesDetectionData[],
  mouseY: number,
  valueScales: { left: any; right: any },
): string | undefined => {
  if (validSeries.length === 0) {
    return undefined;
  }

  let closestSeries = validSeries[0];
  let minDistance = Infinity;

  // Process series in reverse order so that when multiple series have identical values,
  // we pick the one rendered last (on top in SVG)
  for (let i = validSeries.length - 1; i >= 0; i--) {
    const series = validSeries[i];
    const valueScale = valueScales[series.axis];
    const seriesY = valueScale(series.value ?? 0);
    const distance = Math.abs(mouseY - seriesY);

    if (distance < minDistance) {
      minDistance = distance;
      closestSeries = series;
    }
  }

  const FALLBACK_TOLERANCE = 3;
  return minDistance <= FALLBACK_TOLERANCE ? closestSeries.key : undefined;
};

export const detectHoveredSeries = (
  mouseY: number,
  seriesData: SeriesDetectionData[],
  valueScales: { left: any; right: any },
  mouseX?: number,
  barPositions?: Array<{ key: string; x: number; width: number }>,
  isStacked = false,
): string | undefined => {
  if (seriesData.length === 0) {
    return undefined;
  }
  const validSeries = seriesData.filter((s) => s.value !== null && (!isStacked || s.value > 0));
  if (validSeries.length === 0) {
    return undefined;
  }

  const config: SeriesDetectionConfig = {
    mouseX,
    mouseY,
    barPositions,
    isStacked,
  };

  const stackedResult = isStacked
    ? detectStackedSeries(config, validSeries, valueScales)
    : undefined;
  if (stackedResult !== undefined) {
    return stackedResult;
  }

  const groupedBarResult = detectGroupedBarSeries(config, validSeries, valueScales);
  if (groupedBarResult !== undefined) {
    if (groupedBarResult !== null) {
      return groupedBarResult;
    }

    const onlyBarSeries = validSeries.every((series) => series.chartType === 'bar');
    if (onlyBarSeries) {
      return undefined;
    }
  }

  const standardResult = detectStandardSeries(config, validSeries, valueScales);
  if (standardResult !== undefined) {
    return standardResult;
  }

  // 3. Fallback to closest series if nothing else worked
  return detectFallbackSeries(validSeries, mouseY, valueScales);
};
